import { useRef, useCallback, useEffect, useState } from "react";

import Image from "@/components/Atoms/Image";
import SVGSprite from "@/components/Atoms/SVGSprite";
import Typography from "@/foundation/Typography/V2";

import DownloadPDFStrip from "../DownloadPDFStrip";
import OneOnOneYogaPricingSection from "../OneOnOneYogaPricingSection";

import { classNameGenerator, clsx, handleScrollIntoView } from "@/utils";

import { useAppDispatch, useAppSelector, useWindowDimensions } from "@/hooks";
import * as actions from "@/redux/BootCampReducer";

import styles from "./HeroSection.module.scss";
import TimerSection from "../TimerSection";

interface HeroSectionProps { }

const HeroSection: React.FC<HeroSectionProps> = () => {
  const dispatch = useAppDispatch();
  const [isMobile, setIsMobile] = useState(false)
  const [sortedSchedule, setSortedSchedule] = useState([])
  const { width } = useWindowDimensions();
  const { data } = useAppSelector((state) => state.bootCamp);

  useEffect(() => {
    setIsMobile(width < 600)
  }, [width])

  const pageType = data?.pageType;
  const { currencySymbol, price } = data?.packData || {};

  const setShowBookSlotModal = useCallback(
    (value: any) => {
      dispatch(actions.setShowBookSlotModal(value));
    },
    [dispatch]
  );

  const isBadgeAlignment = ["90-days-transformation"].includes(data?.slug)

  const {
    title,
    heroSectionTitle,
    description,
    secondaryDescription,
    packData,
    labelData,
    thumbnail,
    mobileThumbnail,
    styleData,
    allPackages,
  } = data || {};
  const { showLabel, label } = labelData || {};
  const { startDate } = packData || {};
  const { summary, secondarySummary, ctaText, seccondaryCtaText = "", benefitSection, showOneOnOnePricingSection, benefitSubHeading, hideDate = false, hideCta = false, hideBenefitSectionClassWeb = "", showTimerInHeroSection = false, showBenefitsInHeroSection = false, classSchedule } =
    data?.heroSection || {};
  const [isPageLoaded,setIsPageLoaded] = useState(true)
  const { isLoading: dataLoading }  = useAppSelector((state) => state.bootCamp);
  
  const heroCardContainerRef = useRef<HTMLDivElement>(null);
  const HeroDetailContainerRef = useRef<HTMLDivElement>(null);
  const tncEnabled = data?.termsAndCondition && true

  const {
    hasLightDescription,
    backgroundColor,
    maxWidth,
    hasLightPrimaryCta,
    hasLightHeadingMainText,
    showGradient,
    rightShiftPosition,
  } = styleData || {};

  useEffect(() => {
    setIsPageLoaded(dataLoading)
  },[dataLoading])

  const cls = classNameGenerator(styles);

  useEffect(() => {
    if (width >= 600) {
      const groupedByTime = {};

      (classSchedule || [])?.forEach(([day, time]) => {
        if (!groupedByTime[time]) {
          groupedByTime[time] = [];
        }
        groupedByTime[time].push(day);
      });

      const schedule = Object.entries(groupedByTime).map(([time, days]) => {
        return [days, time];
      });
      setSortedSchedule(schedule)
    } else {
      setSortedSchedule(classSchedule)
    }
  }, [width])


  return (
    <section id="BootCampHeroSection">
      <div className={clsx(cls("heroSectionContainer"), cls(hideBenefitSectionClassWeb))}>
        <div
          className={cls("heroWrapper")}
          style={{
            background: backgroundColor,
            "--hero-bg-color": backgroundColor,
            "--hero-content-max-width": maxWidth,
          }}
        >
          <div className={cls("heroTopSectionContainer")}>
            <div
              ref={heroCardContainerRef}
              className={cls("heroCardContainer")}
              style={{
                right: `${-rightShiftPosition || 0}px`,
              }}
            >
              <div
                className={clsx(
                  cls("heroCardImageWrapper"),
                  showGradient ? cls("showGradient") : ""
                )}
              >
                <Image
                  alt={title}
                  src={thumbnail}
                  className={cls("heroCardImage")}
                />
                <Image
                  alt={title}
                  src={mobileThumbnail || thumbnail}
                  className={cls("mWebHeroCardImage")}
                />
              </div>
            </div>
            <div
              ref={HeroDetailContainerRef}
              className={cls("HeroDetailContainer")}
            >
              <div className={cls("textContainer")}>
                {!isBadgeAlignment && <div className={cls("textWrapper")}>
                  <Typography
                    variant="heading1"
                    className={clsx(
                      cls("pageHeading"),
                      hasLightHeadingMainText
                        ? cls("lightColor")
                        : cls("darkColor")
                    )}
                    dangerouslySetInnerHTML={{ __html: heroSectionTitle }}
                  />
                  {showLabel && (
                    <div
                      className={cls("labelWrapper")}
                      style={{ background: labelData.background }}
                    >
                      {labelData.icon && (
                        <SVGSprite
                          icon={labelData.icon}
                          className={cls("icon")}
                        />
                      )}
                      <Typography
                        variant="paragraph4"
                        className={clsx(
                          cls("titleLabel"),
                          cls(labelData.version)
                        )}
                        style={{ color: labelData.color }}
                      >
                        {label}
                      </Typography>
                    </div>
                  )}
                  {!hideDate && startDate && (
                    <div className={cls("chip")}>Starts from: {startDate}</div>
                  )}
                </div>}
                {isBadgeAlignment && <div className={cls("textWrapper2")}>
                  <Typography
                    variant="heading1"
                    className={clsx(
                      cls("pageHeading"),
                      hasLightHeadingMainText
                        ? cls("lightColor")
                        : cls("darkColor")
                    )}
                    dangerouslySetInnerHTML={{ __html: heroSectionTitle }}
                  />
                  {showLabel && (
                    <div
                      className={cls("labelWrapper")}
                      style={{ background: labelData.background }}
                    >
                      {labelData.icon && (
                        <SVGSprite
                          fill={labelData?.iconColor}
                          icon={labelData.icon}
                          className={cls("icon")}
                        />
                      )}
                      <Typography
                        variant="paragraph4"
                        className={clsx(
                          cls("titleLabel"),
                          cls(labelData.version)
                        )}
                        style={{ color: labelData.color }}
                      >
                        {label}
                      </Typography>
                    </div>
                  )}
                  {startDate && (
                    <div className={cls("chip")}>Starts from: {startDate}</div>
                  )}
                </div>}
              </div>
              <Typography
                variant="heading4"
                className={clsx(
                  cls("title"),
                  hasLightDescription ? cls("lightColor") : cls("darkColor")
                )}
                dangerouslySetInnerHTML={{ __html: description }}
              />
              <Typography
                variant="paragraph2"
                className={clsx(
                  cls("description"),
                  hasLightDescription ? cls("lightColor") : cls("darkColor")
                )}
                dangerouslySetInnerHTML={{ __html: summary }}
              />
              {secondaryDescription &&
                <Typography
                  variant="heading4"
                  className={clsx(
                    cls("title"), cls('secondary-description-style'),
                    hasLightDescription ? cls("lightColor") : cls("darkColor")
                  )}
                  dangerouslySetInnerHTML={{ __html: secondaryDescription }}
                />
              }
              {secondarySummary &&
                <Typography
                  variant="paragraph2"
                  className={clsx(
                    cls("description"),
                    hasLightDescription ? cls("lightColor") : cls("darkColor")
                  )}
                  dangerouslySetInnerHTML={{ __html: secondarySummary }}
                />
              }
              {
                classSchedule && sortedSchedule &&
                <div className={cls("scheduleContainer")}>
                  <div className={cls("scheduleDetails")}>
                    <h2 className={cls("heading")}>Schedule</h2>
                    <div className={cls("daysContent")}>
                      {
                        sortedSchedule?.map((day) => {
                          if (typeof day[0] === "string") {
                            return (
                              <div className={cls("timRow")}>
                                <Typography
                                  variant="heading4"
                                  className={cls("scheduleDay")}
                                  dangerouslySetInnerHTML={{ __html: day[0] }}
                                />
                                <Typography
                                  variant="paragraph1"
                                  className={cls("classTime")}
                                  dangerouslySetInnerHTML={{ __html: day[1] }}
                                />
                              </div>
                            )
                          } else {
                            return (
                              <div className={cls("timRow")}>
                                <div className={cls("timRow-web")}>
                                  {day[0]?.map((_val, index) => {
                                    return (
                                      <>
                                        <Typography
                                          variant="heading4"
                                          className={cls("scheduleDay")}
                                          dangerouslySetInnerHTML={{ __html: _val }}
                                        />
                                        {index !== day[0]?.length - 1 &&
                                          <Typography
                                            variant="paragraph1"
                                            className={cls("partitioner")}
                                          > | </Typography>
                                        }
                                      </>
                                    )
                                  })}
                                </div>
                                <Typography
                                  variant="paragraph1"
                                  className={cls("classTime")}
                                  dangerouslySetInnerHTML={{ __html: day[1] }}
                                />
                              </div>
                            )
                          }
                        })
                      }

                    </div>
                  </div>
                  <button
                    type="button"
                    className={clsx(
                      cls("heroCtaButton"),
                      hasLightPrimaryCta
                        ? cls("lightColor")
                        : cls("darkColor")
                    )}
                    disabled={isPageLoaded}
                    onClick={() =>
                      setShowBookSlotModal({
                        packageData: data?.packData,
                        showSubtitle: true,
                        showTnc: { tncEnabled }
                      })
                    }
                  >
                    <span className={cls("heroCtaButtonText")}>
                      {ctaText}
                    </span>
                    <span>{arrowIcon}</span>
                  </button>
                </div>
              }
              {showBenefitsInHeroSection && <div className={cls("heroBenefits")}>
                {benefitSection?.map((item: any) => {
                  return (
                    <div
                      key={item?.label}
                      className={cls("heroBenefitCard")}
                    >
                      <div className={cls("benefitTopSection")}>
                        {item?.icon && <SVGSprite icon={item?.icon} />}
                      </div>
                      <Typography
                        variant="paragraph4"
                        className={cls("benefitText")}
                        dangerouslySetInnerHTML={{ __html: item?.label }}
                      />
                    </div>
                  );
                })}
              </div>}
              {showTimerInHeroSection && <TimerSection
                icon="icon-offer-light"
                ctaIconColor="black"
              />}
              <div
                className={clsx(
                  cls("ctaSectionWrapper"),
                  cls("desktopContainer")
                )}
              >
                {!hideDate && startDate && (
                  <Typography variant="paragraph4" className={cls("ctaLabel")}>
                    Programs starts on {startDate}
                  </Typography>
                )}
                {!hideCta && <div className={cls("heroCtaWrapper")}>
                  {ctaText && (
                    <button
                      type="button"
                      className={clsx(
                        cls("heroCtaButton"),
                        hasLightPrimaryCta
                          ? cls("lightColor")
                          : cls("darkColor")
                      )}
                      disabled={isPageLoaded}
                      onClick={() =>
                        setShowBookSlotModal({
                          packageData: data?.packData,
                          showSubtitle: true,
                          showTnc: { tncEnabled }
                        })
                      }
                    >
                      {pageType === "NUTRITION_BOOTCAMP" ? (
                        (currencySymbol && price!==null && <span
                          className={cls("heroCtaButtonText")}
                          dangerouslySetInnerHTML={{
                            __html: `${ctaText} ${currencySymbol}&nbsp;${price}`,
                          }}
                        />)
                      ) : (
                        <span className={cls("heroCtaButtonText")}>
                          {ctaText}
                        </span>
                      )}
                      <span>{arrowIcon}</span>
                    </button>
                  )}
                  {allPackages?.packages?.length > 0 && (
                    <button
                      type="button"
                      className={clsx(
                        cls("heroViewPlansCtaButton"),
                        hasLightPrimaryCta
                          ? cls("lightColor")
                          : cls("darkColor")
                      )}
                      onClick={() =>
                        handleScrollIntoView({ id: "all-packages-section" })
                      }
                    >
                      <span
                        className={cls("heroCtaButtonText")}
                        dangerouslySetInnerHTML={{
                          __html: `${seccondaryCtaText || "View Plans"}`,
                        }}
                      />
                    </button>
                  )}
                  {showOneOnOnePricingSection && (
                    <div className={cls("requestCallbackWrapper")}>
                      <OneOnOneYogaPricingSection />
                    </div>
                  )}
                </div>}
              </div>
            </div>
          </div>
        </div>
        <div className={cls("downloadPDFWrapper")}>
          <DownloadPDFStrip />
        </div>
        <div className={cls("heroBenefitsContainerWrapper")}>
          {benefitSubHeading &&
            <Typography variant="heading3" className={cls("heroBenefitsContainerSubHeading")}>
              {benefitSubHeading}
            </Typography>}
          {benefitSection?.length > 0 && <div className={cls("heroBenefitsContainer")}>
            {benefitSection?.map((item: any) => {
              return (
                <div
                  key={item?.label}
                  className={clsx(cls("benefitCard"), cls(item?.size))}
                >
                  <div className={cls("topSection")}>
                    <Typography
                      variant="paragraph4"
                      className={cls("percentageText")}
                    >
                      {item?.percentage}
                    </Typography>
                    {item?.icon && <SVGSprite icon={isMobile ? (item?.mobileIcon ? item?.mobileIcon : item?.icon) : item?.icon} />}
                  </div>
                  <Typography
                    variant="paragraph4"
                    className={clsx(
                      cls("benefitText"),
                      item?.icon ? cls("withIcon") : ""
                    )}
                    dangerouslySetInnerHTML={{ __html: item?.label }}
                  />
                </div>
              );
            })}
          </div>
          }
        </div>
      </div>
    </section>
  );
};

export default HeroSection;

const arrowIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="17"
    height="16"
    viewBox="0 0 17 16"
    fill="none"
  >
    <path
      d="M6.5 13L10.5 9L6.5 5"
      stroke="#ffffff"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
